import { BlockNoteEditor, isTableCellSelection } from '@blocknote/core';
import { Block, BlockSchema } from '../schema';
import isImageOrTableBlock from './is-image-or-table-block';

export default function findBlockToSeparateOnEnter(
  editor: BlockNoteEditor<BlockSchema>,
) {
  if (isTableCellSelection(editor.prosemirrorState.selection)) {
    return null;
  }

  const document = [...editor.document];
  const blocksToSeparate = findBlocksNeedingParagraphBeforeNext(document);

  if (blocksToSeparate.length === 0) {
    return null;
  }

  const { block, prevBlock, nextBlock } = editor.getTextCursorPosition();

  for (const leadingBlock of blocksToSeparate) {
    const trailingBlock = findLeadingBlockForPair(document, leadingBlock);

    if (
      trailingBlock &&
      isCursorAtAdjacentMediaBoundary(
        leadingBlock,
        trailingBlock,
        block,
        prevBlock,
        nextBlock,
      )
    ) {
      return leadingBlock;
    }
  }

  if (
    editor.schema.blockSchema[block.type].content === 'none' &&
    nextBlock &&
    isImageOrTableBlock(nextBlock)
  ) {
    return block;
  }

  if (block.type === 'table' && prevBlock && isImageOrTableBlock(prevBlock)) {
    return prevBlock;
  }

  const selection = editor.getSelection();
  if (!selection?.blocks.length) {
    return null;
  }

  const selectedIds = new Set(
    selection.blocks.map((selectedBlock) => selectedBlock.id),
  );

  for (const leadingBlock of blocksToSeparate) {
    const trailingBlock = findLeadingBlockForPair(document, leadingBlock);

    if (
      trailingBlock &&
      (selectedIds.has(leadingBlock.id) || selectedIds.has(trailingBlock.id))
    ) {
      return leadingBlock;
    }
  }

  return null;
}

function findBlocksNeedingParagraphBeforeNext(document: Array<Block>) {
  const blocksNeedingParagraphAfter: Array<Block> = [];

  for (let index = 0; index < document.length - 1; index += 1) {
    const block = document[index];
    const nextBlock = document[index + 1];

    if (
      block &&
      nextBlock &&
      isImageOrTableBlock(block) &&
      isImageOrTableBlock(nextBlock)
    ) {
      blocksNeedingParagraphAfter.push(block);
    }
  }

  return blocksNeedingParagraphAfter;
}

function findLeadingBlockForPair(document: Array<Block>, leadingBlock: Block) {
  const leadingIndex = document.findIndex(
    (documentBlock) => documentBlock.id === leadingBlock.id,
  );
  return document[leadingIndex + 1] ?? null;
}

function isCursorAtAdjacentMediaBoundary(
  leadingBlock: Block,
  trailingBlock: Block,
  block: Block,
  prevBlock: Block | undefined,
  nextBlock: Block | undefined,
) {
  if (block.id === leadingBlock.id && nextBlock?.id === trailingBlock.id) {
    return true;
  }

  if (block.id === trailingBlock.id && prevBlock?.id === leadingBlock.id) {
    return true;
  }

  if (prevBlock?.id === leadingBlock.id && nextBlock?.id === trailingBlock.id) {
    return true;
  }

  if (block.id === leadingBlock.id || block.id === trailingBlock.id) {
    return true;
  }

  return false;
}
